import { ReactNode } from 'react';

import { CAST_AI } from '@cast/constants';
import {
  mergeSx,
  SxProps,
  ExternalLink,
  Typography,
  Box,
  Stack,
  colorOrToken,
  tokens,
} from '@cast/design-system';

import { AgentCodeSnippet } from 'components/code';

import { getDocsLink } from '@cast/utils';

type ScriptSectionProps = {
  script: string | undefined;
  showReadOnlyAgentScriptDisclaimer?: boolean;
  disclaimer?: ReactNode;
  isLoading: boolean;
  refetch(): void;
  error: unknown;
  overlay?: ReactNode;
  customScriptContent?: ReactNode;
  scriptFooter?: ReactNode;
  sx?: SxProps;
  scriptSx?: SxProps;
};

type ScriptSectionLayoutProps = Omit<
  ScriptSectionProps,
  'showReadOnlyAgentScriptDisclaimer' | 'disclaimer'
> & {
  children: ReactNode;
};

type ScriptContainerProps = Omit<
  ScriptSectionLayoutProps,
  'children' | 'scriptFooter' | 'sx'
>;

const ScriptContainer = ({
  script,
  isLoading,
  refetch,
  error,
  overlay,
  customScriptContent,
  scriptSx,
}: ScriptContainerProps) => {
  if (customScriptContent) {
    return customScriptContent;
  }

  return (
    <Box position="relative" sx={{ overflow: 'hidden' }}>
      <Box
        sx={{
          transition: 'opacity 0.3s ease',
          opacity: isLoading ? 0.4 : 1,
        }}
      >
        <AgentCodeSnippet
          code={script}
          isLoading={isLoading}
          refetch={refetch}
          error={error}
          skeletonHeight={200}
          sx={scriptSx}
        />
      </Box>
      {overlay}
    </Box>
  );
};

const ScriptSectionLayout = ({
  script,
  isLoading,
  refetch,
  error,
  children,
  overlay,
  customScriptContent,
  scriptFooter,
  sx,
  scriptSx,
}: ScriptSectionLayoutProps) => {
  return (
    <Stack
      sx={mergeSx(
        {
          gap: 8,
        },
        sx
      )}
    >
      {children}
      <Stack gap={4}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          width="100%"
          mt={8}
        >
          <Typography
            variant="L10M"
            color={colorOrToken(
              'grey.400',
              tokens.theme.fg.structural.secondary
            )}
          >
            Run the script:
          </Typography>
          <Typography variant="P10M">
            <ExternalLink link={getDocsLink('docs_kubernetes_permissions')}>
              Kubernetes permissions
            </ExternalLink>
          </Typography>
        </Stack>
        <Stack>
          <ScriptContainer
            script={script}
            isLoading={isLoading}
            refetch={refetch}
            error={error}
            overlay={overlay}
            customScriptContent={customScriptContent}
            scriptSx={scriptSx}
          />
          {scriptFooter}
        </Stack>
      </Stack>
    </Stack>
  );
};

export const ScriptSection = ({
  showReadOnlyAgentScriptDisclaimer = true,
  disclaimer,
  ...props
}: ScriptSectionProps) => {
  return (
    <ScriptSectionLayout {...props}>
      <Typography variant="P12R" color="textSecondary">
        Ensure kubectl and Helm are installed locally. Copy and run the script
        below in your cloud shell or terminal.
      </Typography>
      {showReadOnlyAgentScriptDisclaimer && (
        <Typography variant="P12R" color="textSecondary">
          {disclaimer ?? (
            <>
              This script installs {CAST_AI} &apos;s read-only agent and Spot
              interruption handler. Your sensitive data remains secure, and your
              cluster configuration unchanged.
            </>
          )}
        </Typography>
      )}
    </ScriptSectionLayout>
  );
};
